import type { DraftDto, DraftKind, MailIdentity, Message } from "@/api/mailApi";

// Shape of state the ComposePane is opened with. Reply / forward all use the
// same machinery — only the initial values differ.
export interface ComposeSeed {
    kind: DraftKind;
    identityId: string | null;
    to: string;
    cc: string;
    bcc: string;
    subject: string;
    bodyHtml: string;
    inReplyToMessageId: string | null;
    attachmentIds: string[];
}

export function seedForNew(identities: MailIdentity[]): ComposeSeed {
    return {
        kind: "New",
        identityId: defaultIdentityId(identities),
        to: "",
        cc: "",
        bcc: "",
        subject: "",
        bodyHtml: signatureBlock(defaultIdentity(identities)),
        inReplyToMessageId: null,
        attachmentIds: []
    };
}

export function seedForReply(
    message: Message,
    identities: MailIdentity[],
    replyAll: boolean
): ComposeSeed {
    const me = defaultIdentity(identities);
    const replyTo = message.from.email;

    let cc = "";
    if (replyAll) {
        const extras = message.to
            .map((r) => r.email)
            .filter((email) => email && email.toLowerCase() !== (me?.emailAddress ?? "").toLowerCase())
            .filter((email) => email.toLowerCase() !== replyTo.toLowerCase());
        cc = extras.join(", ");
    }

    return {
        kind: replyAll ? "ReplyAll" : "Reply",
        identityId: me?.id ?? null,
        to: replyTo,
        cc,
        bcc: "",
        subject: ensurePrefix("Re:", message.subject),
        bodyHtml: signatureBlock(me) + buildQuote(message),
        inReplyToMessageId: message.id,
        attachmentIds: []
    };
}

// Hydrate compose from a saved draft so clicking a Drafts-folder row reopens
// exactly what was last stored, including any unsent quoted body.
export function seedForDraft(draft: DraftDto): ComposeSeed {
    return {
        kind: draft.kind,
        identityId: draft.identityId,
        to: draft.to,
        cc: draft.cc,
        bcc: draft.bcc,
        subject: draft.subject,
        bodyHtml: draft.bodyHtml,
        inReplyToMessageId: draft.inReplyToMessageId,
        attachmentIds: draft.attachmentIds
    };
}

export function seedForForward(message: Message, identities: MailIdentity[]): ComposeSeed {
    const me = defaultIdentity(identities);
    return {
        kind: "Forward",
        identityId: me?.id ?? null,
        to: "",
        cc: "",
        bcc: "",
        subject: ensurePrefix("Fwd:", message.subject),
        bodyHtml: signatureBlock(me) + buildQuote(message),
        inReplyToMessageId: message.id,
        attachmentIds: []
    };
}

function defaultIdentity(identities: MailIdentity[]): MailIdentity | undefined {
    return identities.find((i) => i.isDefault) ?? identities[0];
}

function defaultIdentityId(identities: MailIdentity[]): string | null {
    return defaultIdentity(identities)?.id ?? null;
}

export function signatureBlock(identity: MailIdentity | undefined): string {
    if (!identity?.signatureHtml) return "<p><br /></p>";
    return `<p><br /></p><div data-fg-signature="true">${identity.signatureHtml}</div>`;
}

function ensurePrefix(prefix: string, subject: string): string {
    const trimmed = subject.trim();
    if (trimmed.toLowerCase().startsWith(prefix.toLowerCase())) return trimmed;
    return `${prefix} ${trimmed}`;
}

/**
 * The quoted original, laid out as Outlook lays it out: a rule, a From / Sent / To / Cc / Subject
 * block, and the original body exactly as it was. The body used to go inside a grey, indented
 * blockquote that recoloured all of its text, so a forwarded message lost its formatting and
 * looked different to every reader.
 */
function buildQuote(message: Message): string {
    const sent = new Date(message.receivedAt).toLocaleString();
    const person = (a: { name?: string | null; email: string }) =>
        a.name ? `${escapeHtml(a.name)} &lt;${escapeHtml(a.email)}&gt;` : escapeHtml(a.email);
    const list = (xs: { name?: string | null; email: string }[] | null | undefined) =>
        (xs ?? []).map(person).join("; ");
    const to = list(message.to);
    const cc = list(message.cc);
    const head = "font-family:Calibri,Carlito,Arial,sans-serif;font-size:11pt;color:#000000";
    return [
        `<div data-fg-quote="1">`,
        `<hr style="display:inline-block;width:98%" tabindex="-1" />`,
        `<div style="${head}">`,
        `<b>From:</b> ${person(message.from)}<br />`,
        `<b>Sent:</b> ${escapeHtml(sent)}<br />`,
        to ? `<b>To:</b> ${to}<br />` : "",
        cc ? `<b>Cc:</b> ${cc}<br />` : "",
        `<b>Subject:</b> ${escapeHtml(message.subject)}`,
        `</div>`,
        `<div>&nbsp;</div>`,
        message.bodyHtml,
        `</div>`
    ].filter(Boolean).join("\n");
}

function escapeHtml(value: string): string {
    return value
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
}
